import { type ElementType, type ReactNode } from 'react';

import { cx } from '@/utils/cx';

import {
    FormInputContainerRecipeProps,
    formInputContainerRecipe,
} from './formInputContainer.styles';

interface FormInputContainerProps<T extends ElementType> extends FormInputContainerRecipeProps {
    Component?: T;
    // classNames
    className?: string;
    startControlsClassName?: string;
    endControlsClassName?: string;
    // optional controls
    startControls?: ReactNode;
    endControls?: ReactNode;
    children?: ReactNode; // override RAC children which allows a render function
}

const FormInputContainer = <T extends ElementType = 'div'>({
    Component,
    className,
    startControlsClassName,
    endControlsClassName,
    startControls,
    endControls,
    children,
    ...rest
}: FormInputContainerProps<T>) => {
    const [variantProps, localProps] = formInputContainerRecipe.splitVariantProps(rest);
    const formInputContainerClasses = formInputContainerRecipe(variantProps);

    const ContainerComponent = Component || 'div';

    return (
        <ContainerComponent
            className={cx(formInputContainerClasses.root, className)}
            {...localProps}>
            {startControls ? (
                <FormInputContainerActions className={startControlsClassName} {...variantProps}>
                    {startControls}
                </FormInputContainerActions>
            ) : null}
            {children}
            {endControls ? (
                <FormInputContainerActions className={endControlsClassName} {...variantProps}>
                    {endControls}
                </FormInputContainerActions>
            ) : null}
        </ContainerComponent>
    );
};

type FormInputContainerActionsProps = FormInputContainerRecipeProps & {
    className?: string;
    children?: ReactNode;
};

const FormInputContainerActions = ({
    children,
    className,
    ...rest
}: FormInputContainerActionsProps) => {
    const formInputClasses = formInputContainerRecipe(rest);
    return <div className={cx(formInputClasses.actions, className)}>{children}</div>;
};

export { FormInputContainer };
export type { FormInputContainerProps };
